import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GetFormulaDetailDocument } from "~/graphql/supergraphHooks";
import {
  buildFormulaComponentAccordionItems,
  collectReferencedFormulaIds,
  MAX_COMPONENT_TREE_DEPTH,
  type FormulaComponentAccordionItem,
  type FormulaDetail,
} from "./formulaComponentItems";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { Formula } from "../../FormulasGrid";
import type { TypeHierarchy } from "../../HierarchyTreeGrid";
import type {
  GetFormulaDetailQuery,
  GetFormulaDetailQueryVariables,
} from "~/graphql/supergraphTypes";

type UseFormulaComponentItemsArgs = {
  arenaId: number | null;
  formulaId: number | null | undefined;
  typeHierarchy: TypeHierarchy | null | undefined;
  formulas: ReadonlyArray<Formula>;
  attributeLeaves: ReadonlyArray<AttributeLeaf>;
  enabled: boolean;
};

export type UseFormulaComponentItemsResult = {
  accordionItems: ReadonlyArray<FormulaComponentAccordionItem>;
  isLoading: boolean;
  /** False when any requested formula detail resolved to null (archived /
   * hidden / deleted sub-formula) or the fetch threw. A missing detail is
   * an unknown, not proof of removal, so consumers must not prune saved
   * per-component config while the expansion is incomplete. */
  isExpansionComplete: boolean;
  /** True once a fetch attempt for the CURRENT formula produced a detail
   * map. False before the first fetch resolves, after a thrown fetch, while
   * disabled, or while the state still belongs to a previously displayed
   * formula — states where empty `accordionItems` means "unknown", not "the
   * formula has no leaf components". */
  hasResolvedExpansion: boolean;
};

type ExpansionState = {
  /** Formula id the state was produced for. When the mounted panel switches
   * straight from one formula field to another, the state from the previous
   * formula survives until the refetch effect runs; deriving through this
   * tag (compared synchronously during render) keeps that stale state from
   * ever being exposed as the new formula's expansion. */
  forFormulaId: number | null;
  detailsByFormulaId: ReadonlyMap<number, FormulaDetail> | null;
  isComplete: boolean;
};

const fetchFormulaDetail = async (
  client: ReturnType<typeof useApolloClient>,
  variables: GetFormulaDetailQueryVariables,
) => {
  const result = await client.query<GetFormulaDetailQuery, GetFormulaDetailQueryVariables>({
    query: GetFormulaDetailDocument,
    variables,
    // network-only: the expansion must reflect the latest saved components.
    // Formula edits elsewhere (e.g. adding a second usage of one node) do
    // not reliably update the cached detail, and a stale cache hit would
    // silently drop component occurrences from the panel.
    fetchPolicy: "network-only",
  });
  return result.data.finpubFormula ?? null;
};

/** Fetches the formula's full expansion (breadth-first, one wave of
 * `GetFormulaDetail` calls per nesting level) and builds the path-keyed
 * accordion items. Cycles terminate because each formula id is fetched at
 * most once; the wave loop and the item builder share
 * `MAX_COMPONENT_TREE_DEPTH`, so anchors at the max depth are neither
 * fetched nor expanded. */
export const useFormulaComponentItems = ({
  arenaId,
  formulaId,
  typeHierarchy,
  formulas,
  attributeLeaves,
  enabled,
}: UseFormulaComponentItemsArgs): UseFormulaComponentItemsResult => {
  const client = useApolloClient();
  const [expansion, setExpansion] = useState<ExpansionState>({
    forFormulaId: null,
    detailsByFormulaId: null,
    isComplete: true,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!enabled || formulaId == null) {
        setExpansion({ forFormulaId: null, detailsByFormulaId: null, isComplete: true });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const details = new Map<number, FormulaDetail>();
        // A successful response can still carry `finpubFormula: null`
        // (archived / hidden / deleted / concurrent edit). Track those
        // misses: the expansion is only complete when every requested
        // detail resolved.
        let hasMisses = false;
        // Wave N holds the formulas whose components sit at tree depth N.
        let wave: number[] = [formulaId];
        for (let depth = 1; depth <= MAX_COMPONENT_TREE_DEPTH && wave.length > 0; depth++) {
          const fetched = await Promise.all(
            wave.map((id) => fetchFormulaDetail(client, { arenaId, formulaId: id })),
          );
          if (cancelled) return;
          wave.forEach((id, index) => {
            const detail = fetched[index];
            if (detail) {
              details.set(id, detail);
            } else {
              hasMisses = true;
            }
          });

          const next = new Set<number>();
          for (const id of wave) {
            const detail = details.get(id);
            if (!detail) continue;
            for (const referencedId of collectReferencedFormulaIds(detail)) {
              if (!details.has(referencedId)) next.add(referencedId);
            }
          }
          wave = [...next];
        }

        if (!cancelled) {
          setExpansion({
            forFormulaId: formulaId,
            detailsByFormulaId: details,
            isComplete: !hasMisses,
          });
        }
      } catch {
        if (!cancelled) {
          setExpansion({
            forFormulaId: formulaId,
            detailsByFormulaId: null,
            isComplete: false,
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [arenaId, client, enabled, formulaId]);

  // Synchronous staleness guard: on the render where `formulaId` changes
  // (before the refetch effect above has even run), the state still belongs
  // to the previous formula. A mismatched tag reads as the initial
  // not-yet-fetched state so consumers never seed, prune, or latch against
  // the prior field's expansion.
  const isCurrentExpansion = expansion.forFormulaId === (formulaId ?? null);
  const detailsByFormulaId = isCurrentExpansion ? expansion.detailsByFormulaId : null;
  const isExpansionComplete = isCurrentExpansion ? expansion.isComplete : true;

  const accordionItems = useMemo(() => {
    if (formulaId == null || !detailsByFormulaId) return [];
    return buildFormulaComponentAccordionItems(
      formulaId,
      detailsByFormulaId,
      typeHierarchy,
      attributeLeaves,
      formulas,
    );
  }, [detailsByFormulaId, formulaId, typeHierarchy, attributeLeaves, formulas]);

  return {
    accordionItems,
    isLoading,
    isExpansionComplete,
    hasResolvedExpansion: detailsByFormulaId != null,
  };
};
